import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Animating 2048 SVG nodes in React, Preact, Inferno, Vue, Angular 2, and CycleJS – a side-by-side comparison",
  "description": "",
  "published": "2016-12-21T00:00:00.000Z",
  "redirect_from": ["/blog/animating-svg-nodes-react-preact-inferno-vue/swizec/7311"],
  "categories": "AngularJS, cyclejs, inferno, preact, react, Technical, vue",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Ever wondered which front-end framework is smoothest to animate 2048 SVG nodes? Here are some GIFs.`}</p>
    <p>{`Same `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/dancing-tree-fractal-react/swizec/7288"
      }}>{`dancing pythagorean tree fractal`}</a>{`, same mid-2012 retina MacBook Pro. All recorded with LICEcap in a normal, everyday setting running Chrome, Spotify, Emacs, etc. Click on a GIF to see its code.`}</p>
    <p>{`-`}{`-- `}<em parentName="p">{`Angular 2 and CycleJS added on Dec 23rd`}</em>{` ---`}</p>
    <p>{`People have been asking `}<em parentName="p">{`why`}</em>{` this test is important, `}<em parentName="p">{`why`}</em>{` should they care, `}<em parentName="p">{`what's the point`}</em>{`. It's fun and the demo looks pretty. Fundamentally, it's a showcase of "jank" – how much jankiness is there between what you see on the screen and what you do with the mouse.`}</p>
    <p>{`Many of the gifs have live versions linked from their github. Play with them. Maybe even clone the repo and run it locally. It's fun.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/react-tree-small.gif",
        "alt": "Implementation: mine"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/preact-tree-small.gif",
        "alt": "preact-tree-small"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/inferno-tree-small.gif",
        "alt": "Implementation: Dominic Gannaway, creator of Inferno"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/vue-tree-small.gif",
        "alt": "Implementation: Evan You, creator of Vue"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/ng2-tree-small.gif",
        "alt": "Implementation: Tero Parviainen, JavaScript consultant"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/cyclejs-tree-small-1.gif",
        "alt": "Implementation: Wayne Maurer, founder of Lambda IT"
      }}></img></p>
    <p>{`Thanks to Jason, Dominic, and Evan for building the forks. You guys rock!`}</p>
    <p>{`And thanks to Tero and Wayne for adding their own versions! So exciting! I hope we get more ? Someone mentioned a raw JavaScript version without frameworks; that would be cool.`}</p>
    <p>{`Let's look at the code. For an in-depth explanation of how it works, go `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/dancing-tree-fractal-react/swizec/7288"
      }}>{`here`}</a>{`.`}</p>
    <h2 {...{
      "id": "react"
    }}>{`React`}</h2>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/react-fractals"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2016/12/react-tree-small.gif",
          "alt": "react-tree-small"
        }}></img></a></p>
    <p>{`Based on Jason's and Evan's tips, I added mouse event throttling to my demo to make it faster. Turns out my original tree wasn't so slow because React is slow. It was slow because I was slamming the rendering engine with so many requests per refresh cycle.`}</p>
    <p>{`I tried throttling to `}<inlineCode parentName="p">{`requestAnimationFrame`}</inlineCode>{`, but that didn't work so good. Throttling to React's redraw cycle was easy and works well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`onMouseMove(event) {
    if (this.running) return;
    this.running = true;

    // calculate stuff

    this.setState({
        heightFactor: scaleFactor(y),
        lean: scaleLean(x)
    });
    this.running = false;
}
`}</code></pre>
    <p>{`Check if update is running, do update if not. This works because React's engine is synchronous.`}</p>
    <p>{`It might stop working when React Fiber is out. I think. ¯`}{`\\`}<em parentName="p">{`(ツ)`}</em>{`/¯`}</p>
    <h2 {...{
      "id": "preact"
    }}>{`Preact`}</h2>
    <p><a parentName="p" {...{
        "href": "https://github.com/developit/preact-fractals"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2016/12/preact-tree-small.gif",
          "alt": "preact-tree-small"
        }}></img></a></p>
    <p>{`Jason used the `}<inlineCode parentName="p">{`preact-compat`}</inlineCode>{` layer to make Preact pretend that it's React. This might impact performance.`}</p>
    <p>{`What I love about the Preact example is that it uses async rendering to look smoother. You can see the redraw cycle lag behind the mouse movement producing curious effects.`}</p>
    <p>{`I like it.`}</p>
    <p>{`Here's how he did it: `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-fractals/compare/master...developit:master"
      }}>{`diff on github`}</a></p>
    <p>{`In `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`, he added `}<inlineCode parentName="p">{`preact`}</inlineCode>{`, `}<inlineCode parentName="p">{`preact-compat`}</inlineCode>{`, and preact -compat clones for React libraries. I guess you need the latter so you don't have to change your imports.`}</p>
    <p>{`He changed the functional stateless `}<inlineCode parentName="p">{`Pythagoras`}</inlineCode>{` component into a stateful component to enable async rendering.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/Pythagoras.js
export default class {
  render(props) {
    return Pythagoras(props);
  }
}
`}</code></pre>
    <p>{`And enabled debounced asynchronous rendering:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/index.js
import { options } from "preact";
options.syncComponentUpdates = false;

//option 1:  rIC + setTimeout fallback
let timer;
options.debounceRendering = (f) => {
  clearTimeout(timer);
  timer = setTimeout(f, 100);
  requestIdleCallback(f);
};
`}</code></pre>
    <p>{`My favorite part is that you can use Preact as a drop-in replacement for React and it Just Works `}<em parentName="p">{`and`}</em>{` works well. Very promising for future performance optimizations in my current apps.`}</p>
    <h2 {...{
      "id": "inferno"
    }}>{`Inferno`}</h2>
    <p><a parentName="p" {...{
        "href": "https://github.com/trueadm/inferno-fractals"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2016/12/inferno-tree-small.gif",
          "alt": "inferno-tree-small"
        }}></img></a></p>
    <p>{`You `}<em parentName="p">{`can`}</em>{` use Inferno as a drop-in replacement for React, and at first I did. Dominic says that impacts performance though, so he made a proper fork. You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-fractals/compare/master...trueadm:master"
      }}>{`diff on github`}</a>{`.`}</p>
    <p>{`Dominic changed all `}<inlineCode parentName="p">{`react-scripts`}</inlineCode>{` references to `}<inlineCode parentName="p">{`inferno-scripts`}</inlineCode>{`, and it's a good sign that such a thing exists. He also changed `}<inlineCode parentName="p">{`react`}</inlineCode>{` to `}<inlineCode parentName="p">{`inferno-beta36`}</inlineCode>{`, which means my CTO definitely won't let me use it in production yet.`}</p>
    <p>{`From there, the main changes are to the imports – React becomes Inferno – and he changed some class methods to bound fat arrow functions. I don't know if that's a stylistic choice or an Inferno requirement.`}</p>
    <p>{`He also had to change a string-based ref into a callback ref. Inferno doesn't do string-based refs for performance reasons, and we need them so we can use D3 to detect mouse position on SVG. It's easier than doing it ourselves.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/App.js

class App extends Component {
    // ...
    svgElemeRef = (domNode) => {
        this.svgElement = domNode;
    }
    // ...
    render() {
        // ..

    }
`}</code></pre>
    <p>{`In the core `}<inlineCode parentName="p">{`Pythagoras`}</inlineCode>{` component, he added two Inferno-specific props: `}<inlineCode parentName="p">{`noNormalize`}</inlineCode>{` and `}<inlineCode parentName="p">{`hasNonKeyedChildren`}</inlineCode>{`.`}</p>
    <p>{`According to `}<a parentName="p" {...{
        "href": "https://github.com/trueadm/inferno/issues/565"
      }}>{`this issue`}</a>{` from 8 days ago, `}<inlineCode parentName="p">{`noNormalize`}</inlineCode>{` is a benchmark-focused flag that improves performance, and I can't figure out what `}<inlineCode parentName="p">{`hasNonKeyedChildren`}</inlineCode>{` does. I assume both are performance optimizations for the Virtual DOM diffing algorithm.`}</p>
    <h2 {...{
      "id": "vue"
    }}>{`Vue`}</h2>
    <p><a parentName="p" {...{
        "href": "https://github.com/yyx990803/vue-fractal"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2016/12/vue-tree-small.gif",
          "alt": "vue-tree-small"
        }}></img></a></p>
    <p>{`This… this took a lot of work. Kudos to Evan and the original forker Phan An!`}</p>
    <p>{`Vue doesn't aim to mimic React's API, so this is an almost complete rewrite. I'd show you the diff on Github, but it won't let me do that. You can `}<a parentName="p" {...{
        "href": "https://github.com/yyx990803/vue-fractal"
      }}>{`see the code though`}</a>{`.`}</p>
    <p>{`You can still recognize the `}<a parentName="p" {...{
        "href": "https://github.com/yyx990803/vue-fractal/blob/master/src/components/Pythagoras.js"
      }}>{`core `}<inlineCode parentName="a">{`Pythagoras`}</inlineCode>{` component`}</a>{`. Evan used `}<inlineCode parentName="p">{`transform-vue-jsx`}</inlineCode>{` to enable JSX inside Vue. So we know that's something you can do.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/yyx990803/vue-fractal/blob/master/src/App.vue"
      }}>{`main `}<inlineCode parentName="a">{`App.vue`}</inlineCode>{` file`}</a>{` though… I'm not used to reading that, so I can't really explain the code. `}<em parentName="p">{`But it looks so familiar.`}</em></p>
    <p>{`Let's try.`}</p>
    <p>{`It's split into a `}<inlineCode parentName="p">{`<template>`}</inlineCode>{`, `}<inlineCode parentName="p">{`<script>`}</inlineCode>{` and `}<inlineCode parentName="p">{`<style>`}</inlineCode>{` section. Looks a bit like JSX or HTML, but template props are prefixed with a colon.`}</p>
    <p>{`Looks like Vue took the put-it-all-together componentization cue from React but decided to split by language anyway. I don't know if that's good or bad. It `}<em parentName="p">{`looks`}</em>{` cleaner, but in my experience, splitting those eventually becomes burdensome.`}</p>
    <p>{`The App component still looks roughly like it used to, but it uses `}<inlineCode parentName="p">{`data()`}</inlineCode>{` to define default state, `}<inlineCode parentName="p">{`$refs`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`this.refs`}</inlineCode>{`, a `}<inlineCode parentName="p">{`name`}</inlineCode>{` attribute instead of naming the class itself, a `}<inlineCode parentName="p">{`components`}</inlineCode>{` attribute to define children, and a `}<inlineCode parentName="p">{`methods`}</inlineCode>{` attribute to define class methods.`}</p>
    <p>{`Curious ?`}</p>
    <h2 {...{
      "id": "angular-2"
    }}>{`Angular 2`}</h2>
    <p><a parentName="p" {...{
        "href": "https://github.com/teropa/ng-fractals"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2016/12/ng2-tree-small.gif",
          "alt": "Implementation: Tero Parviainen, JavaScript consultant"
        }}></img></a></p>
    <p>{`I've never been a fan of Angular, but this looks pretty good. Although it did feel extremely janky on my machine.`}</p>
    <p>{`I don't know why. Maybe all that type checking from TypeScript adds runtime overhead after transpiling?`}</p>
    <p>{`The code is a major rewrite, obviously. Tero had to port it all into TypeScript and I find that impressive. I sure wouldn't ?`}</p>
    <p>{`I wonder how the language barrier affects reusability of random libraries you find online ?`}</p>
    <p>{`The code also looks like it has many more files. `}<inlineCode parentName="p">{`App`}</inlineCode>{` is split into `}<inlineCode parentName="p">{`app.module.ts`}</inlineCode>{`, `}<inlineCode parentName="p">{`app.component.ts`}</inlineCode>{`, `}<inlineCode parentName="p">{`app.component.html`}</inlineCode>{`, and `}<inlineCode parentName="p">{`app.component.css`}</inlineCode>{`. Same with `}<inlineCode parentName="p">{`Pythagoras`}</inlineCode>{`.`}</p>
    <p>{`This implies Angular is sticking with the traditional one-language-per-file webdev separation ... although when you look at an Angular `}<inlineCode parentName="p">{`html`}</inlineCode>{` file ...`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<div class="App-header">
  <h2>This is a dancing Pythagoras tree</h2>
</div>
<p class="App-intro">
  <svg #svg="" [attr.width]="width" [attr.height]="height" style="border: 1px solid lightgray">
    <g app-pythagoras="" [w]="baseW" [heightfactor]="heightFactor" [lean]="lean" [x]="width / 2 - 40" [y]="height - baseW" [lvl]="0" [maxlvl]="currentMax"></g>
  </svg>
</p>
`}</code></pre>
    <p>{`That's some funny looking HTML.`}</p>
    <p>{`I'm also not sure I understand the difference between a module and a component. It looks like the module declares certain imports, children components, and stuff. But each component still declares its own CSS and template imports.`}</p>
    <p>{`Smells like a framework optimized for very large teams. I've never been a part of one of those, so I can't say how good it is for the use-case it was designed for.`}</p>
    <h2 {...{
      "id": "cyclejs"
    }}>{`CycleJS`}</h2>
    <p><a parentName="p" {...{
        "href": "https://github.com/wmaurer/cyclejs-fractals"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2016/12/cyclejs-tree-small-1.gif",
          "alt": "Implementation: Wayne Maurer, founder of Lambda IT"
        }}></img></a></p>
    <p>{`Now this… this was smooth on my machine. Maybe it just feels smooth because I looked at it right after the Angular version, but damn.`}</p>
    <p>{`Wayne translated everything into TypeScript, but it doesn't look like CycleJS `}<em parentName="p">{`requires`}</em>{` that. Despite that, he was able to keep the same simple file structure as the original. I like that.`}</p>
    <p>{`It's hard for me to tell what he changed because of TypeScript and what's different because of CycleJS. Looks like he's not using classes to define CycleJS components. The structure looks more like the old school closure approach.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export function App(sources: Sources): Sinks {
    const factorAndLean$ = sources.DOM.select('#the-svg') //...

    const args$ = xs.combine(factorAndLean$, xs.periodic(500) //...

    const pythagoras$ = Pythagoras(args$);

    const vtree$ = pythagoras$.map(x =>
        div(Styles.App, [ // ...

    return {
        DOM: vtree$
    };
}
`}</code></pre>
    <p>{`This would take some getting used to.`}</p>
    <p>{`The part I really dislike is how Wayne specifies the DOM in CycleJS. The main App looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`div(Styles.App, [
  div(Styles.AppHeader, [
    img(Styles.AppLogo, { attrs: { src: "cyclejs_logo.svg" } }),
    h2("This is a dancing Pythagoras tree"),
  ]),
  p(Styles.AppIntro, [
    svg(
      "#the-svg",
      {
        attrs: {
          height: svgDimensions.height,
          width: svgDimensions.width,
          style: "border: 1px solid lightgray",
        },
      },
      [x]
    ),
  ]),
]);
`}</code></pre>
    <p>{`Looks a lot like using the `}<inlineCode parentName="p">{`React.createElement`}</inlineCode>{` approach and I find that hard to read. CycleJS does support JSX however, so I'm not sure why he's not using it.`}</p>
    <p>{`That said, the end result is phenomenal.`}</p>
    <p>{`Edit on Dec 24th: as @spion pointed out, it turns out the CycleJS example only renders 2^10 rectangles. Half as much as the other examples, due to how `}<inlineCode parentName="p">{`.take`}</inlineCode>{` works. This has a huge effect on the jankiness.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`just checked, document.getElementsByTagName('rect').length does indeed return just 1024 :)`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/812724325392318464"
      }}>{`December 24, 2016`}</a></blockquote>
    <p>{`Edit 2: fixed the code in a local copy, updated the gif, still smooth as hell. I'm impressed.`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`I have no idea which is fastest. They all look smooth to me. Maaaybe Vue is the smoothest. Or maybe Inferno. I like that Preact enables asynchronous rendering. That was cool to see.I'm biased against Angular, and found CycleJS hella impressive.`}</p>
    <p>{`React is king in the “Good Enough And I Already Know How To Use It” department.`}</p>
    <p>{`I don't foresee migrating my code to Vue, Angular, or CycleJS any time soon. Too much work. Preact or Inferno as drop-ins? Waiting to see what React Fiber does.`}</p>
    <p>{`My biggest takeaway from this experiment though: Buy a new computer. When others made gifs it looked smooth as hell!`}</p>
    <p>{`If you want to `}<strong parentName="p">{`learn`}</strong>{` more about `}<strong parentName="p">{`drawing interactive pictures`}</strong>{`, building `}<strong parentName="p">{`animations`}</strong>{`, and creating `}<strong parentName="p">{`declarative data vizualisations`}</strong>{`, you should pre-order my new `}<a parentName="p" {...{
        "href": "https://gumroad.com/l/mTWg"
      }}>{`React+D3v4 book`}</a>{`. It's coming out soon, and there will be a chapter on leveraging Preact and Inferno for speed optimization.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      